import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { FiAward, FiTool, FiZap, FiShield } from "react-icons/fi";
import Tilt from "../cardItems/Tilt";
import Stats from "../stats/Stats";
import Services from "./Services";
import { whatsappLink } from "../../contact";
import "./About.css";

const HERO_TITLE = "Experts en Acier Inoxydable & Innovation".split(" ");

// TODO: adjust these steps to match the real workshop process.
const STEPS = [
  {
    title: "Étude et conception",
    text: "Nous écoutons votre besoin, conseillons les meilleures solutions en inox et validons le design avec vous.",
  },
  {
    title: "Découpe et façonnage",
    text: "L'inox est découpé et formé avec précision selon les dimensions exactes de votre projet.",
  },
  {
    title: "Soudure et assemblage",
    text: "Chaque pièce est assemblée avec soin pour garantir solidité et finitions impeccables.",
  },
  {
    title: "Finition et contrôle qualité",
    text: "Polissage, vérification et contrôle strict avant toute livraison.",
  },
  {
    title: "Livraison et installation",
    text: "Transport sécurisé et pose sur site, avec un emballage soigné pour éviter tout dommage.",
  },
];

const REASONS = [
  { icon: FiAward, title: "Matériaux premium", text: "Un inox de qualité supérieure pour des réalisations durables." },
  { icon: FiTool, title: "Sur mesure", text: "Fabrication à vos dimensions, avec des finitions impeccables." },
  { icon: FiZap, title: "Innovation", text: "Des solutions modernes pour l'industrie et le design." },
  { icon: FiShield, title: "Durabilité", text: "Un engagement constant envers la performance et la longévité." },
];

const Abouttest = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(heroProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(heroProgress, [0, 1], ["0%", "60%"]);
  const textOpacity = useTransform(heroProgress, [0, 0.8], [1, 0]);

  const timelineRef = useRef(null);
  const { scrollYProgress: lineProgress } = useScroll({
    target: timelineRef,
    offset: ["start 75%", "end 60%"],
  });

  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="ab">
      <section className="ab-hero" ref={heroRef}>
        <motion.div className="ab-hero__bg" style={{ y: bgY }}>
          <video
            src="/vedios/vedio1.mp4"
            poster="/vedios/poster-about.jpg"
            autoPlay
            muted
            loop
            playsInline
          />
        </motion.div>
        <div className="ab-hero__shade" />
        <motion.div className="ab-hero__content" style={{ y: textY, opacity: textOpacity }}>
          <motion.span
            className="ab-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            À propos de nous
          </motion.span>
          <h1 className="ab-hero__title" aria-label={HERO_TITLE.join(" ")}>
            {HERO_TITLE.map((word, i) => (
              <span className="ab-word" key={i} aria-hidden="true">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, ease: [0.2, 0.65, 0.3, 0.9], delay: 0.2 + i * 0.1 }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>
        </motion.div>
      </section>

      <section className="ab-intro">
        <div className="ab-intro__text">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Votre Partenaire en Inox
          </motion.h2>
          <motion.p
            className="ab-lead"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            Qualité, durabilité et expertise au service de vos projets
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Chez <strong>Cristal Inox</strong>, nous sommes spécialisés dans la
            conception et la fabrication de structures en acier inoxydable,
            alliant design et robustesse. Grâce à notre savoir-faire unique,
            nous répondons aux besoins des secteurs de l'architecture, du
            médical et de l'industrie.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Nous mettons en avant des solutions sur mesure adaptées aux
            exigences les plus strictes, garantissant qualité, résistance et
            finitions haut de gamme.
          </motion.p>
          <button type="button" className="ab-btn" onClick={scrollToServices}>
            Découvrir Nos Services
          </button>
        </div>
        <motion.div
          className="ab-intro__logo"
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
        >
          <img src="/images/logo1.png" alt="Cristal Inox" />
        </motion.div>
      </section>

      <Stats />

      <section className="ab-process">
        <h2 className="ab-heading">Notre savoir-faire, étape par étape</h2>
        <div className="ab-timeline" ref={timelineRef}>
          <div className="ab-timeline__track">
            <motion.div className="ab-timeline__fill" style={{ scaleY: lineProgress }} />
          </div>
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              className={`ab-step${i % 2 ? " is-right" : ""}`}
              initial={{ opacity: 0, x: i % 2 ? 60 : -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <span className="ab-step__num">{String(i + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="ab-why">
        <h2 className="ab-heading">Pourquoi choisir Cristal Inox ?</h2>
        <div className="ab-why__grid">
          {REASONS.map(({ icon: Icon, title, text }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
            >
              <Tilt className="ab-why__tilt">
                <div className="ab-why__card">
                  <span className="ab-why__icon">
                    <Icon />
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      <Services />

      <section className="ab-cta">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>Un projet en inox en tête ?</h2>
          <p>Parlons-en : devis gratuit et conseils personnalisés.</p>
          <div className="ab-cta__buttons">
            <Link to="/contact" className="ab-btn ab-btn--light">
              Demander un devis
            </Link>
            <a
              href={whatsappLink("Bonjour, j'ai un projet en inox et je souhaite en discuter.")}
              target="_blank"
              rel="noopener noreferrer"
              className="ab-btn ab-btn--whatsapp"
            >
              WhatsApp
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Abouttest;
